import type { HandleAttestation } from "../proto/HandleAttestation";
import { useIsMobile, useMediaQuery } from "../lib/media";
import type { Client } from "../proto/Client";
import type { Group } from "../proto/Group";
import type { Member } from "../proto/Member";
import type { Platform } from "../proto/Platform";
import { hoistedRole, nameColor, permissionsOf, rankOf } from "../lib/permissions";
import { useSettings } from "../state/settings";
import { useUi } from "../state/stores";

/** Windows at least this wide show the member list by default. */
const WIDE_QUERY = "(min-width: 1100px)";

/**
 * Whether the member list is shown and how to toggle it. Wide windows follow
 * the remembered setting (open by default only when there is room); phones
 * show it as a page of its own over the conversation.
 */
export function useMembersPanel() {
  const isMobile = useIsMobile();
  const wide = useMediaQuery(WIDE_QUERY);
  const preference = useSettings((s) => s.membersOpen);
  const setPreference = useSettings((s) => s.setMembersOpen);
  const page = useUi((s) => s.pane === "members");
  const setPane = useUi((s) => s.setPane);
  const open = isMobile ? page : (preference ?? wide);
  return { open, isMobile, toggle: () => (isMobile ? setPane(page ? "chat" : "members") : setPreference(!open)) };
}

export interface MemberEntry {
  uid: string;
  nickname: string;
  tag: string;
  connect?: HandleAttestation | null;
  admin: boolean;
  online: boolean;
  platform: Platform | null;
  /** Voice channel name, for people in voice. */
  channel: string | null;
  /** Unix ms; only meaningful for offline members. */
  lastSeen: number;
  groups: number[];
  /** Session id while online. */
  session: number | null;
  /** Colour of the highest role that has one. */
  color: string | undefined;
  /** Position of their best role (lower is higher), for ordering. */
  rank: number;
}

const isAdmin = (groups: number[], defs: Record<number, Group>) => permissionsOf(groups, defs).includes("administrator");
const byRankThenName = (a: MemberEntry, b: MemberEntry) =>
  a.rank - b.rank || a.nickname.localeCompare(b.nickname, undefined, { sensitivity: "base" }) || a.uid.localeCompare(b.uid);

/** Online people under a role shown separately (`hoist`), highest role first. */
export interface HoistedSection {
  role: Group;
  entries: MemberEntry[];
}

/**
 * Splits everyone the server knows into people in voice, online (under their highest role shown
 * separately, if any) and offline; within each, higher ranks first.
 */
export function groupMembers(
  clients: Client[],
  members: Member[],
  groups: Record<number, Group>,
  channelName: (id: number) => string | null,
): { voice: MemberEntry[]; hoisted: HoistedSection[]; online: MemberEntry[]; offline: MemberEntry[] } {
  // One entry per person: the session in voice wins over a second device that is not.
  const sessions = new Map<string, Client>();
  for (const c of clients) {
    const known = sessions.get(c.uid);
    if (!known || (known.channel === null && c.channel !== null)) sessions.set(c.uid, c);
  }
  const known = new Map(members.map((m) => [m.uid, m]));
  const voice: MemberEntry[] = [];
  const online: MemberEntry[] = [];
  for (const c of sessions.values()) {
    const entry: MemberEntry = {
      uid: c.uid,
      nickname: known.get(c.uid)?.nickname ?? c.nickname,
      tag: known.get(c.uid)?.tag ?? "",
      connect: known.get(c.uid)?.connect,
      admin: isAdmin(c.groups, groups),
      online: true,
      platform: c.platform,
      channel: c.channel === null ? null : channelName(c.channel),
      lastSeen: 0,
      groups: c.groups,
      session: c.id,
      color: nameColor(c.groups, groups),
      rank: rankOf({ groups: c.groups }, groups),
    };
    (c.channel === null ? online : voice).push(entry);
  }
  const sections = new Map<number, HoistedSection>();
  const rest: MemberEntry[] = [];
  for (const entry of online) {
    const role = hoistedRole(entry.groups, groups);
    if (!role) {
      rest.push(entry);
      continue;
    }
    const section = sections.get(role.id) ?? { role, entries: [] };
    section.entries.push(entry);
    sections.set(role.id, section);
  }
  const hoisted = [...sections.values()].sort((a, b) => a.role.position - b.role.position);
  for (const section of hoisted) section.entries.sort(byRankThenName);
  const offline: MemberEntry[] = members
    .filter((m) => !sessions.has(m.uid))
    .map((m) => ({
      uid: m.uid,
      nickname: m.nickname,
      tag: m.tag,
      connect: m.connect,
      admin: isAdmin(m.groups, groups),
      online: false,
      platform: null,
      channel: null,
      lastSeen: m.last_seen,
      groups: m.groups,
      session: null,
      color: nameColor(m.groups, groups),
      rank: rankOf({ groups: m.groups }, groups),
    }));
  return { voice: voice.sort(byRankThenName), hoisted, online: rest.sort(byRankThenName), offline: offline.sort(byRankThenName) };
}
